import { type MouseEvent, useCallback, useReducer } from 'react';
import { type AxiosError } from 'axios';

import { type CartWithAddedItem } from '@jsmdg/cart-library-ts';
import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    AvailabilitiesCalendarSubtype,
    type CheckoutCategorySubtype,
    type ClickType,
} from '@jsmdg/tracking';
import { ProductType } from '../../shared/enums';
import {
    getBookableLocationsCount,
    isExperience,
    isValueVoucherWithPriceRange,
} from '../../shared/product-safeguards';
import { type Price } from '../../shared/types';
import {
    trackAddToCartClicked,
    trackEECAddToCartAdded,
} from '../components/BuyboxContainer/buyboxContainer.tracking';
import { sendAlgoliaProductAddedEvent } from '../helper/algoliaAnalytics';
import { dispatchEvent } from '../helper/EventUtils';
import { getPurchaseType } from '../helper/getPurchaseType';
import {
    dispatchPurchaseType,
    dispatchResetErrors,
    handleErrorByCode,
} from '../reducers/actions/buyboxReducerActions';
import { buyboxReducer } from '../reducers/buyboxReducer';
import { eventTypes } from '../types';
import { PurchaseType } from '../types/enums/purchaseType';
import { BuyboxReducerActionType } from '../types/enums/reducer/buyboxReducerAction';
import { SellOption } from '../types/enums/sellOption';
import { type ProductDetailContext } from '../types/types';
import { useInsightsClient } from './useInsightsClient';
import { usePrice } from './usePrice';
import { useProduct } from './useProduct';

type AddToCartClickParams = {
    price: Price;
    event?: MouseEvent<HTMLButtonElement>;
    clickType?: ClickType;
    categorySubtype?: AvailabilitiesCalendarSubtype | CheckoutCategorySubtype;
};

type HandleAddToCartClick = (
    params: AddToCartClickParams & { withNavigateToCart?: boolean },
) => Promise<CartWithAddedItem | undefined>;

type UseBuyboxReturn = {
    purchaseType?: PurchaseType;
    isAddToCartDisabled: (type: PurchaseType | undefined) => boolean;
    hasCookieWarning?: boolean;
    hasMixedShoppingCartError?: boolean;
    hasAddingMultipleItemsError?: boolean;
    hasAddToCartError?: boolean;
    ticketPurchaseIsSelected: (type: PurchaseType | undefined) => boolean;
    handlePurchaseType: (value: PurchaseType) => void;
    handleAddToCartClick: HandleAddToCartClick;
    handleModalClose: () => void;
    handleRedirectOnAppointmentBooking: ({
        event,
        clickType,
        clickText,
    }: {
        event?: MouseEvent<HTMLButtonElement>;
        clickType?: ClickType;
        clickText: string;
    }) => void;
    handlePayPalAddToCartError: (errorCode?: number) => void;
};

const ticketPurchaseIsSelected = (typeOfPurchase: PurchaseType | undefined): boolean => {
    return typeOfPurchase === PurchaseType.Ticket;
};

const useBuybox = (): UseBuyboxReturn => {
    const { insightsClient } = useInsightsClient();

    const product = useProduct();
    const {
        price: { multiplier },
    } = usePrice();
    const bookableLocationsCount = getBookableLocationsCount(product);
    const locations = isExperience(product) ? product.locations : [];
    const sellOption = isExperience(product) ? product.sellOption : undefined;
    const {
        addItemToCart,
        algoliaConfig,
        createAddToCartButtonClickedEvent,
        isBoxProductWithQrCodeLink,
    } = useFragmentContext<ProductDetailContext>();

    const { hasFriendsDiscount, inPreview, originalPrice, productId, type } = product;

    const [
        {
            purchaseType,
            hasCookieWarning,
            hasMixedShoppingCartError,
            hasAddingMultipleItemsError,
            hasAddToCartError,
        },
        dispatchBuybox,
    ] = useReducer(buyboxReducer, {
        purchaseType: getPurchaseType(sellOption),
    });

    const handleRedirectOnAppointmentBooking = ({
        event,
        clickType,
        clickText,
    }: {
        event?: MouseEvent<HTMLButtonElement>;
        clickType?: ClickType;
        clickText: string;
    }): void => {
        event?.preventDefault();
        const experienceId = locations.find(location => location.activeAppointments)?.experienceId;
        const isSingleLocationExperience = bookableLocationsCount === 1 && Boolean(experienceId);
        trackAddToCartClicked(clickType, clickText);

        if (isSingleLocationExperience) {
            window.location.assign(`/availabilities/${product.productId}/${experienceId}`);
            return;
        }

        window.location.assign(`/experiences/${productId}`);
    };

    const handleAddToCartClick: HandleAddToCartClick = async ({
        price,
        event,
        clickType,
        withNavigateToCart = true,
        categorySubtype,
    }) => {
        event?.preventDefault();
        if (!navigator?.cookieEnabled) {
            dispatchBuybox({ type: BuyboxReducerActionType.CookieWarning });
            return undefined;
        }

        const valueVoucherPrice = isValueVoucherWithPriceRange(product) ? price.gross : undefined;

        const promise = addItemToCart({
            productId,
            quantity: multiplier,
            valueVoucherPrice,
        });

        await window.yieldToMainThread();

        if (categorySubtype !== AvailabilitiesCalendarSubtype.CALENDAR_VIEW) {
            void trackAddToCartClicked(clickType, undefined, categorySubtype);
        }

        trackEECAddToCartAdded(
            product,
            price,
            locations,
            multiplier,
            hasFriendsDiscount,
            purchaseType,
            categorySubtype,
        );

        if (insightsClient) {
            sendAlgoliaProductAddedEvent({
                insightsClient,
                productId,
                objectData: [
                    {
                        price: price.gross.amount,
                        quantity: multiplier,
                        discount: originalPrice
                            ? Number((originalPrice.gross.amount - price.gross.amount).toFixed(2))
                            : undefined,
                    },
                ],
                value: price.gross.amount,
                currency: price.gross.currencyCode,
                userToken: algoliaConfig.userToken,
            });
        }

        try {
            const result = await promise;

            await createAddToCartButtonClickedEvent({ productId, cartId: result.cart.cartId });
            if (withNavigateToCart) {
                dispatchEvent(eventTypes.NAVIGATE_CART);
            } else {
                return result;
            }
        } catch (error) {
            const response: { errorCode?: number } | unknown = (error as AxiosError).response?.data;
            handleErrorByCode(dispatchBuybox, (response as { errorCode?: number })?.errorCode);
        }

        return undefined;
    };

    return {
        handleRedirectOnAppointmentBooking,
        purchaseType,
        isAddToCartDisabled: (typeOfPurchase): boolean => {
            if (
                !bookableLocationsCount &&
                ProductType.EXPERIENCE === type &&
                (sellOption === SellOption.TicketOnly || typeOfPurchase === PurchaseType.Ticket)
            ) {
                return true;
            }

            return Boolean(inPreview || isBoxProductWithQrCodeLink);
        },
        hasCookieWarning,
        hasMixedShoppingCartError,
        hasAddingMultipleItemsError,
        hasAddToCartError,
        handlePurchaseType: (value: PurchaseType) => dispatchPurchaseType(dispatchBuybox, value),
        handleAddToCartClick,
        handleModalClose: () => dispatchResetErrors(dispatchBuybox),
        ticketPurchaseIsSelected: typeOfPurchase => ticketPurchaseIsSelected(typeOfPurchase),
        handlePayPalAddToCartError: useCallback(
            (errorCode?: number) => handleErrorByCode(dispatchBuybox, errorCode),
            [dispatchBuybox],
        ),
    };
};

export { useBuybox };
export type { AddToCartClickParams, HandleAddToCartClick, UseBuyboxReturn };
